import { Type } from 'src/common/organisms/setsanditems/SetsAndItems.styled';
import React, { useState, useRef } from 'react';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
import {
    Brands,
    BrandsName,
    Circle,
    Description,
    ItemListViewWrapper,
    ItemTable,
    ProductCfn,
    ProductContainer,
    ProductData,
    Quantity,
    TableData,
    TableHeader,
    TableRow,
    TypeText,
} from './ItemList.styled';
import { sizes } from 'src/styles/Media.styled';
import { MoreHorizRounded } from '@material-ui/icons';
import { Box, ClickAwayListener, Tooltip } from '@material-ui/core';
import styled from 'styled-components';
import i18n from 'src/i18n.js';
import { useTranslation } from 'react-i18next';
import { logErrorToConsole } from 'src/utils/util';
const t = (key) => i18n.t(key);

// Styled components defined outside the main component
const tooltipClasses = {
    tooltip: 'MuiTooltip-tooltip',
    arrow: 'MuiTooltip-arrow',
    popper: 'MuiTooltip-popper',
};

const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: 'white',
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: 'white',
        color: 'rgba(0, 0, 0, 0.87)',
        maxWidth: 220,
        fontSize: '12px',
        border: '1px solid #dadde9',
        padding: '10px',
        borderRadius: '2px',
        boxShadow: '0px 0px 6px #00000029',
    },
    [`& .${tooltipClasses.popper}`]: {
        zIndex: '999999',
    },
}));

const TooltipContent = styled(Box)`
    display: flex;
    align-items: center;
    flex-direction: row;
    cursor: ${({ cursor }) => cursor && cursor};
`;

const MoreIcon = styled(MoreHorizRounded)`
    width: 10px !important;
    height: 10px !important;
`;

const portalDisabledProps = {
    disablePortal: true,
};

const customPositionProps = (popperRef, anchorRef, clickPosition) => ({
    popperRef,
    anchorEl: {
        getBoundingClientRect: () => {
            const anchorRect = anchorRef.current?.getBoundingClientRect() || {
                top: 0,
                left: 0,
                width: 0,
                height: 0,
            };

            return new DOMRect(
                clickPosition?.x ?? anchorRect.left,
                anchorRect.top,
                0,
                anchorRect.height
            );
        },
    },
});

// Reusable Tooltip component

const TruncatedTooltip = ({
    text,
    maxLength,
    productCfn,
    isOrderScreen,
    isBrand = false,
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [clickPosition, setClickPosition] = React.useState(null);
    const [isTruncated, setIsTruncated] = React.useState(false);
    const anchorRef = React.useRef(null);
    const popperRef = React.useRef(null);
    const textRef = React.useRef(null);

    const currentPopperProps = !isOrderScreen
        ? customPositionProps(popperRef, anchorRef, clickPosition)
        : portalDisabledProps;

    const handleClick = (event) => {
        setClickPosition({ x: event.clientX });
        setIsOpen(!isOpen);

        // Update popper position after state changes
        if (popperRef.current) {
            setTimeout(() => {
                try {
                    popperRef.current.update();
                } catch (error) {
                    logErrorToConsole(error);
                }
            }, 0);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        setClickPosition(null);
    };

    // Check for truncation by comparing scrollWidth and clientWidth
    const checkTruncation = () => {
        if (textRef.current) {
            const isOverflowing =
                textRef.current.scrollWidth > textRef.current.clientWidth;
            setIsTruncated(isOverflowing);
        }
    };

    // useEffectWithErrorLogging to check truncation after rendering
    useEffectWithErrorLogging(() => {
        checkTruncation();
        window.addEventListener('resize', checkTruncation); // Re-check on resize

        // Cleanup
        return () => {
            window.removeEventListener('resize', checkTruncation);
        };
    }, [text, maxLength]); // Dependency on text and maxLength

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Box style={{ width: isBrand ? '94%' : '97%' }}>
                <HtmlTooltip
                    open={isOpen}
                    onClose={handleClose}
                    placement="top"
                    anchorEl={anchorRef.current}
                    PopperProps={currentPopperProps}
                    title={
                        <div>
                            <div
                                style={{
                                    fontWeight: '700',
                                    marginBottom: '3px',
                                }}
                            >
                                {productCfn}
                            </div>
                            <div>{text}</div>
                        </div>
                    }
                    arrow
                >
                    <TooltipContent
                        ref={anchorRef}
                        onClick={isTruncated && handleClick}
                        cursor={isTruncated && 'pointer'}
                    >
                        <span
                            ref={textRef}
                            style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
                        >
                            {text}
                        </span>
                        {isTruncated && (
                            <Circle>
                                <MoreIcon />
                            </Circle>
                        )}
                    </TooltipContent>
                </HtmlTooltip>
            </Box>
        </ClickAwayListener>
    );
};

const ItemListView = ({
    products,
    isOrderScreen,
    handleSetItemClick,
    isReviewSubmitScreen,
}) => {
    const { t } = useTranslation();
    const winWidth = window.innerWidth;
    const isIpadMiniPortrait =
    window.innerWidth === 768 && window.innerHeight === 1024;
    const isMobileView = winWidth <= sizes.smalDevice;
    const isTabletView =
        winWidth >= sizes.smalDevice && winWidth <= sizes.mediumDevice;

    const getMaxLength = (type) => {
        if (type === 'description') {
            return isOrderScreen ? 47 : isTabletView ? 42 : 60;
        }
        if (type === 'brand') {
            return isOrderScreen ? 22 : 30;
        }
        return 0;
    };

    const renderDesktopView = () => (
        <ItemTable>
            <thead>
                <tr>
                    <TableHeader width="17%">
                        {isOrderScreen
                            ? t(
                                  'scheduleSurgery.surgery.notifications.tableView.item'
                              )
                            : t(
                                  'scheduleSurgery.surgery.notifications.tableView.product'
                              )}
                    </TableHeader>
                    <TableHeader width="50%">
                        {t(
                            'scheduleSurgery.surgery.notifications.itemListView.description'
                        )}
                    </TableHeader>
                    <TableHeader width="25%" className="brand">
                        {t(
                            'scheduleSurgery.surgery.notifications.itemListView.brand'
                        )}
                    </TableHeader>
                    <TableHeader className="quantity">
                        {t(
                            'scheduleSurgery.surgery.notifications.itemListView.qtyUom'
                        )}
                    </TableHeader>
                </tr>
            </thead>
            <tbody>
                {products?.map((item, index) => (
                    <TableRow key={index}>
                        <TableData
                            width="17%"
                            fontWeight="700 !important"
                            className="product"
                            onClick={() => {
                                try {
                                    handleSetItemClick(item);
                                } catch (error) {
                                    logErrorToConsole(error);
                                }
                            }}
                        >
                            {item.productCFN || item.cfn || item.bom}
                        </TableData>
                        <TableData width="50%" className="description">
                            {item.description && (
                                <TruncatedTooltip
                                    text={item.description}
                                    maxLength={getMaxLength('description')}
                                    productCfn={
                                        item.productCFN || item.cfn || item.bom
                                    }
                                    isOrderScreen={isOrderScreen}
                                />
                            )}
                        </TableData>
                        <TableData width="25%" className="brand">
                            {item.brand && (
                                <TruncatedTooltip
                                    text={
                                        item?.brand && item?.brand != ''
                                            ? typeof item?.brand === 'string'
                                                ? item.brand
                                                : item?.brand[0]
                                            : ''
                                    }
                                    maxLength={getMaxLength('brand')}
                                    productCfn={
                                        item.productCFN || item.cfn || item.bom
                                    }
                                    isOrderScreen={isOrderScreen}
                                    isBrand={true}
                                />
                            )}
                        </TableData>
                        <TableData className="quantity">
                            {item.quantity}&nbsp;<Type>{item.uom}</Type>
                        </TableData>
                    </TableRow>
                ))}
            </tbody>
        </ItemTable>
    );

    const renderMobileView = () =>
        products?.map((item, index) => (
            <ProductContainer
                key={index}
                borderTop={
                    index === 0 &&
                    '1px solid var(--borders-lines-medium, #0000004D)'
                }
                borderBottom={
                    index === products.length - 1 &&
                    '1px solid var(--borders-lines-medium, #0000004D)'
                }
            >
                <ProductData>
                    <ProductCfn
                        id={`surgery-itemlist-${item.productCFN || item.cfn || item.bom}`}
                        onClick={() => {
                            try {
                                handleSetItemClick(item);
                            } catch (error) {
                                logErrorToConsole(error);
                            }
                        }}
                    >
                        {item.productCFN || item.cfn || item.bom}
                    </ProductCfn>
                    <Quantity>
                        {item.quantity} × <TypeText>{item.uom}</TypeText>
                    </Quantity>
                </ProductData>
                <Description>{item.description}</Description>
                {item.brand && (
                    <Brands>
                        {t(
                            'scheduleSurgery.surgery.notifications.itemListView.brands'
                        )}
                        <BrandsName>{`\u00A0${item.brand}`}</BrandsName>
                    </Brands>
                )}
            </ProductContainer>
        ));

    return (
        <>
            <ItemListViewWrapper
                width={isOrderScreen && '100%'}
                padding={!isOrderScreen && '0 5% 6% 5%'}
            >
                
                 {isIpadMiniPortrait
        ? renderDesktopView()
        : !isMobileView
        ? renderDesktopView()
        : renderMobileView()}
                
            </ItemListViewWrapper>
        </>
    );
};

export default ItemListView;
